/// <reference types="google.maps" />
import React, { useEffect, useRef } from "react";
import { useLanguage } from "../../hooks/useLanguage";

declare global {
  interface Window {
    google: typeof google;
  }
}

import { Loader } from "@googlemaps/js-api-loader";
import { useInView } from "react-intersection-observer";
import { motion } from "framer-motion";

const HELSINKI_COORDINATES = {
  lat: 60.19434303195595,
  lng: 24.956288092113365,
};

const MAPS_URL = `https://www.google.com/maps/dir/?api=1&destination=${HELSINKI_COORDINATES.lat},${HELSINKI_COORDINATES.lng}`;
// Only use the environment variable, don't include a fallback API key in client code
const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";

export default function RestaurantMap() {
  const { t, language } = useLanguage();
  const mapRef = useRef<HTMLDivElement>(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Fallback Google Maps embed URL when API key is missing
  const embedMapUrl = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1983.023456789!2d24.954288092113365!3d60.19434303195595!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46920bc796210691%3A0x400b36d18fc6270!2zU3R1cmVua2F0dSAyOCwgMDA1NTAgSGVsc2lua2k!5e0!3m2!1sen!2sfi!4v1692000000000!5m2!1sen!2sfi`;

  useEffect(() => {
    if (!mapRef.current || !inView) return;

    console.log("API Key:", API_KEY ? "Present" : "Missing");
    console.log("API Key length:", API_KEY.length);

    if (!API_KEY) {
      console.warn(
        "Google Maps API key is missing. Falling back to embedded map."
      );
      if (mapRef.current) {
        mapRef.current.innerHTML = `
          <iframe
            src="${embedMapUrl}"
            width="100%"
            height="100%"
            style="border:0;"
            allowfullscreen=""
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade">
          </iframe>
        `;
      }
      return;
    }

    const loader = new Loader({
      apiKey: API_KEY,
      version: "weekly",
    });

    loader
      .load()
      .then(() => {
        if (!mapRef.current) return;

        const map = new google.maps.Map(mapRef.current, {
          center: HELSINKI_COORDINATES,
          zoom: 17,
          mapTypeId: "roadmap",
        });

        const marker = new google.maps.Marker({
          position: HELSINKI_COORDINATES,
          map: map,
          title: "Wave Of Flavors",
        });

        const infoWindow = new google.maps.InfoWindow({
          content: `
          <div style="padding: 10px;">
            <h3 style="margin: 0; font-size: 16px; font-weight: bold;">Wave Of Flavors</h3>
            <p>Sturenkatu 28, Helsinki</p>
            <p>Open Tuesday-Sunday: 12:00-23:00</p>
            <a href="${MAPS_URL}" target="_blank" style="display: inline-block; margin-top: 5px; padding: 5px 10px; background-color: #007bff; color: #fff; text-decoration: none; border-radius: 4px;">${language === "en" ? "Get Directions" : "Ajo-ohjeet"}</a>
          </div>
        `,
        });

        marker.addListener("click", () => {
          infoWindow.open(map, marker);
          window.open(MAPS_URL, "_blank");
        });
      })
      .catch((error) => {
        console.error("Error loading Google Maps:", error);
        if (mapRef.current) {
          mapRef.current.innerHTML = `
            <div class="flex items-center justify-center h-full bg-gray-100 text-gray-600">
              <div class="text-center">
                <p>Unable to load Google Maps</p>
                <p class="text-sm">Error: ${error.message}</p>
                <a href="${MAPS_URL}" target="_blank" class="inline-block mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition">
                  ${language === "en" ? "Open in Google Maps" : "Avaa Google Mapsissa"}
                </a>
              </div>
            </div>
          `;
        }
      });
  }, [inView, language]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }}
      className="w-full h-[400px] rounded-lg overflow-hidden shadow-lg relative"
    >
      <div ref={mapRef} className="w-full h-full" />
      <a
        href={MAPS_URL}
        target="_blank"
        className="absolute bottom-4 left-4 bg-blue-600 text-white px-4 py-2 rounded shadow-lg hover:bg-blue-700 transition"
      >
        {t("footer.getDirections")}
      </a>
    </motion.div>
  );
}
